import { useState, useEffect } from "react";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import {
  FileText, Save, Eye, Edit3, RefreshCw, FolderOpen, ChevronRight,
  Search, Download, ExternalLink, BookOpen, Code, Shield
} from "lucide-react";
import ReactMarkdown from 'react-markdown';

// All .md files organized by category - CLEANED April 6, 2026
const FILE_CATEGORIES = {
  "Core Documents": [
    { id: "master_plan", name: "MASTER_PLAN.md", label: "Master Plan (Source of Truth)", downloadable: true },
    { id: "prd", name: "PRD.md", label: "Product Requirements", downloadable: true },
    { id: "roadmap", name: "ROADMAP.md", label: "Roadmap", downloadable: true },
    { id: "changelog", name: "CHANGELOG.md", label: "Changelog", downloadable: true },
    { id: "current_state", name: "CURRENT_STATE.md", label: "Current State (All Screens & Flows)", downloadable: true },
    { id: "all_defaults", name: "ALL_DEFAULTS_REFERENCE.md", label: "All Defaults Reference", downloadable: true },
    { id: "next_fork", name: "NEXT_FORK_INSTRUCTIONS.md", label: "Next Fork Instructions", downloadable: true },
  ],
  "Integrations": [
    { id: "beehiiv", name: "BEEHIIV.md", label: "Beehiiv (84 Fields & Tags)", downloadable: true },
    { id: "beehiiv_templates", name: "BEEHIIV_EMAIL_TEMPLATES.md", label: "Beehiiv Email Templates", downloadable: true },
  ],
  "Reference": [
    { id: "logo_map", name: "LOGO_MAP.md", label: "Logo Map", downloadable: true },
    { id: "test_credentials", name: "test_credentials.md", label: "Test Credentials", downloadable: true },
    { id: "design", name: "design_guidelines.json", label: "Design Guidelines", downloadable: true },
  ],
  "Legal": [
    { id: "nda_pilot", name: "NDA_PILOT.md", label: "NDA (Pilot Partner)", isNDA: true, downloadable: true },
    { id: "nda_team", name: "NDA_TEAM.md", label: "NDA (Team)", isNDA: true, downloadable: true },
    { id: "advocate_agreement", name: "advocate_commission_agreement.md", label: "Advocate Commission Agreement", downloadable: true },
    { id: "investor_agreement", name: "investor_agreement.md", label: "Investor Agreement", downloadable: true },
    { id: "pilot_nda", name: "pilot_nda.md", label: "Pilot NDA", downloadable: true },
  ],
};

// Default NDA content
const DEFAULT_NDA_PILOT = `# PHYGITAL PILOT PARTNER NON-DISCLOSURE AGREEMENT
## Document ID: PHYGITAL-NDA-PILOT-2026

**Effective Date:** Upon digital signature

---

## PARTIES

This Non-Disclosure Agreement ("Agreement") is entered into by and between:

**Phygital Systems LLC** ("Company")
A Delaware Limited Liability Company
Los Angeles, California

AND

**The undersigned individual** ("Pilot Partner")

---

## 1. PURPOSE

The Pilot Partner has been invited to participate in Phygital's pilot program to test and provide feedback on the Phygital platform ("Platform"). This Agreement protects confidential information shared during the pilot period.

---

## 2. CONFIDENTIAL INFORMATION

"Confidential Information" includes but is not limited to:

- Platform features, functionality, and user interface designs
- Business strategies, roadmaps, and go-to-market plans
- Technical architecture, APIs, and system specifications
- User data structures and preference models
- Pricing models and revenue projections
- Partner and investor relationships
- Any information marked as "Confidential" or "Proprietary"

---

## 3. OBLIGATIONS

The Pilot Partner agrees to:

a) Keep all Confidential Information strictly confidential
b) Not disclose Confidential Information to any third party
c) Not use Confidential Information for any purpose other than participating in the pilot program
d) Not copy, reproduce, or distribute any Confidential Information
e) Return or destroy all Confidential Information upon request or termination of the pilot

---

## 4. EXCEPTIONS

This Agreement does not apply to information that:

a) Is or becomes publicly available through no fault of the Pilot Partner
b) Was known to the Pilot Partner prior to disclosure
c) Is independently developed without use of Confidential Information
d) Is required to be disclosed by law (with prior notice to Company)

---

## 5. TERM

This Agreement remains in effect for **two (2) years** from the date of signature, regardless of whether the Pilot Partner continues to participate in the pilot program.

---

## 6. FEEDBACK AND INTELLECTUAL PROPERTY

Any feedback, suggestions, or ideas provided by the Pilot Partner regarding the Platform shall become the exclusive property of Phygital Systems LLC. The Pilot Partner waives any claim to ownership of such feedback.

---

## 7. NO LICENSE

Nothing in this Agreement grants the Pilot Partner any rights, title, or interest in the Platform, its technology, or any Confidential Information beyond the limited right to participate in the pilot program.

---

## 8. REMEDIES

The Pilot Partner acknowledges that breach of this Agreement may cause irreparable harm to the Company. The Company shall be entitled to seek injunctive relief in addition to any other legal remedies.

---

## 9. GOVERNING LAW

This Agreement shall be governed by the laws of the State of Delaware, without regard to conflict of law principles.

---

## 10. ENTIRE AGREEMENT

This Agreement constitutes the entire agreement between the parties regarding confidentiality and supersedes all prior discussions or agreements.

---

## ACCEPTANCE

By signing below, the Pilot Partner acknowledges that they have read, understood, and agree to be bound by the terms of this Agreement.

**Pilot Partner Signature:** _____________________

**Printed Name:** _____________________

**Date:** _____________________

**Email:** _____________________

---

*Phygital Systems LLC*
*Los Angeles, California*
*hello@phygital.systems*
`;

const DEFAULT_NDA_TEAM = `# PHYGITAL TEAM MEMBER AGREEMENT
## Document ID: PHYGITAL-TEAM-AGREE-2026

**Effective Date:** Upon digital signature

---

## PARTIES

This Team Member Agreement ("Agreement") is entered into by and between:

**Phygital Systems LLC** ("Company")
A Delaware Limited Liability Company
Los Angeles, California

AND

**The undersigned individual** ("Team Member")

---

## 1. ENGAGEMENT

The Team Member is engaged as a [contractor/advisor/team member] to provide services to Phygital Systems LLC.

---

## 2. CONFIDENTIALITY

All confidential information, trade secrets, and proprietary data shared during the engagement shall be kept strictly confidential. This includes but is not limited to:

- Product roadmaps and feature plans
- Technical architecture and code
- Business strategies and financial data
- User data and platform analytics
- Partner and investor information

---

## 3. INTELLECTUAL PROPERTY

All work product, inventions, and intellectual property created during the engagement shall be the exclusive property of Phygital Systems LLC.

---

## 4. NON-COMPETE

During the engagement and for six (6) months thereafter, the Team Member agrees not to engage in any business that directly competes with Phygital's core platform offering.

---

## 5. TERM

This Agreement remains in effect for the duration of the engagement and for two (2) years thereafter regarding confidentiality obligations.

---

## ACCEPTANCE

**Team Member Signature:** _____________________

**Printed Name:** _____________________

**Date:** _____________________

---

*Phygital Systems LLC*
`;

export default function CoreFilesSection() {
  const [selectedCategory, setSelectedCategory] = useState("Core");
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileContent, setFileContent] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [viewMode, setViewMode] = useState("preview"); // preview or edit
  const [searchQuery, setSearchQuery] = useState("");

  const fetchFileContent = async (file) => {
    setLoading(true);
    setSelectedFile(file);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/core-files/${file.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setFileContent(data.content || "");
      } else {
        // For NDA files, use defaults if not found
        if (file.id === "nda_pilot") {
          setFileContent(DEFAULT_NDA_PILOT);
        } else if (file.id === "nda_team") {
          setFileContent(DEFAULT_NDA_TEAM);
        } else {
          setFileContent("// File content not found. Click Edit to add content.");
        }
      }
    } catch (err) {
      // Use defaults for NDA
      if (file.id === "nda_pilot") {
        setFileContent(DEFAULT_NDA_PILOT);
      } else if (file.id === "nda_team") {
        setFileContent(DEFAULT_NDA_TEAM);
      } else {
        setFileContent("// Error loading file");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!selectedFile) return;
    setSaving(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/core-files/${selectedFile.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ 
          content: fileContent,
          path: selectedFile.path,
          name: selectedFile.name
        })
      });
      if (res.ok) {
        toast.success("File saved successfully!");
      } else {
        toast.error("Failed to save file");
      }
    } catch (err) {
      toast.error("Error saving file");
    } finally {
      setSaving(false);
    }
  };

  // Filter files by search
  const getFilteredFiles = () => {
    if (!searchQuery) return FILE_CATEGORIES;
    
    const filtered = {};
    Object.entries(FILE_CATEGORIES).forEach(([category, files]) => {
      const matchingFiles = files.filter(f => 
        f.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
        f.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
      if (matchingFiles.length > 0) {
        filtered[category] = matchingFiles;
      }
    });
    return filtered;
  };

  const filteredCategories = getFilteredFiles();
  const allFiles = Object.values(FILE_CATEGORIES).flat();

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold">Core Files</h2>
          <p className="text-sm text-gray-500">All documentation, NDA templates, and configuration files</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Search files..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 w-64"
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* File Browser */}
        <Card className="lg:col-span-1">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Files ({allFiles.length})</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-[600px]">
              {Object.entries(filteredCategories).map(([category, files]) => (
                <div key={category} className="border-b last:border-0">
                  <div 
                    className="px-4 py-2 bg-gray-50 font-medium text-sm text-gray-600 flex items-center gap-2 cursor-pointer hover:bg-gray-100"
                    onClick={() => setSelectedCategory(category)}
                  >
                    <FolderOpen className="w-4 h-4" />
                    {category}
                    <Badge variant="outline" className="ml-auto text-xs">{files.length}</Badge>
                  </div>
                  <div className="py-1">
                    {files.map(file => (
                      <div key={file.id} className="flex items-center group">
                        <button
                          onClick={() => fetchFileContent(file)}
                          className={`flex-1 px-4 py-2 text-left text-sm flex items-center gap-2 hover:bg-purple-50 transition-colors ${
                            selectedFile?.id === file.id ? "bg-purple-100 text-purple-700" : ""
                          }`}
                        >
                          {file.isNDA ? (
                            <Shield className="w-4 h-4 text-amber-500" />
                          ) : (
                            <FileText className="w-4 h-4 text-gray-400" />
                          )}
                          <span className="truncate">{file.label}</span>
                        </button>
                        {file.downloadable && (
                          <a
                            href={`${API}/admin/core-files/${file.id}/download`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-2 py-1 mr-1 opacity-0 group-hover:opacity-100 transition-opacity"
                            title={`Download ${file.name}`}
                            data-testid={`download-${file.id}`}
                          >
                            <Download className="w-3.5 h-3.5 text-gray-400 hover:text-purple-600" />
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* File Viewer/Editor */}
        <Card className="lg:col-span-3">
          {selectedFile ? (
            <>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      {selectedFile.isNDA && <Shield className="w-5 h-5 text-amber-500" />}
                      {selectedFile.label}
                    </CardTitle>
                    <CardDescription className="font-mono text-xs">{selectedFile.name}</CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    {selectedFile.downloadable && (
                      <a
                        href={`${API}/admin/core-files/${selectedFile.id}/download`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-purple-600 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors"
                        data-testid="download-current-file"
                      >
                        <Download className="w-4 h-4" />
                        Download
                      </a>
                    )}
                    <div className="flex bg-gray-100 rounded-lg p-1">
                      <button
                        onClick={() => setViewMode("preview")}
                        className={`px-3 py-1 rounded text-sm ${viewMode === "preview" ? "bg-white shadow-sm" : ""}`}
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setViewMode("edit")}
                        className={`px-3 py-1 rounded text-sm ${viewMode === "edit" ? "bg-white shadow-sm" : ""}`}
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                    </div>
                    <Button onClick={handleSave} disabled={saving || viewMode === "preview"}>
                      {saving ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                      Save
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-12">
                    <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
                  </div>
                ) : viewMode === "preview" ? (
                  <ScrollArea className="h-[500px] pr-4">
                    <div className="prose prose-sm max-w-none">
                      <ReactMarkdown
                        components={{
                          h1: ({node, ...props}) => <h1 className="text-2xl font-bold mt-6 mb-4" {...props} />,
                          h2: ({node, ...props}) => <h2 className="text-xl font-bold mt-5 mb-3 text-purple-700" {...props} />,
                          h3: ({node, ...props}) => <h3 className="text-lg font-semibold mt-4 mb-2" {...props} />,
                          h4: ({node, ...props}) => <h4 className="text-base font-semibold mt-3 mb-2" {...props} />,
                          p: ({node, ...props}) => <p className="mb-3 text-gray-700" {...props} />,
                          ul: ({node, ...props}) => <ul className="list-disc pl-5 mb-3 space-y-1" {...props} />,
                          ol: ({node, ...props}) => <ol className="list-decimal pl-5 mb-3 space-y-1" {...props} />,
                          li: ({node, ...props}) => <li className="text-gray-700" {...props} />,
                          strong: ({node, ...props}) => <strong className="font-bold" {...props} />,
                          code: ({node, inline, ...props}) => 
                            inline ? (
                              <code className="bg-gray-100 px-1 py-0.5 rounded text-sm text-purple-600" {...props} />
                            ) : (
                              <code className="block bg-gray-900 text-gray-100 p-4 rounded-lg text-sm overflow-x-auto" {...props} />
                            ),
                          blockquote: ({node, ...props}) => (
                            <blockquote className="border-l-4 border-purple-500 pl-4 italic text-gray-600 my-4" {...props} />
                          ),
                          table: ({node, ...props}) => (
                            <div className="overflow-x-auto my-4">
                              <table className="min-w-full border-collapse border border-gray-200" {...props} />
                            </div>
                          ),
                          th: ({node, ...props}) => (
                            <th className="border border-gray-200 px-4 py-2 bg-gray-50 font-semibold text-left" {...props} />
                          ),
                          td: ({node, ...props}) => (
                            <td className="border border-gray-200 px-4 py-2" {...props} />
                          ),
                          hr: ({node, ...props}) => <hr className="my-6 border-gray-200" {...props} />,
                        }}
                      >
                        {fileContent}
                      </ReactMarkdown>
                    </div>
                  </ScrollArea>
                ) : (
                  <textarea
                    value={fileContent}
                    onChange={(e) => setFileContent(e.target.value)}
                    className="w-full h-[500px] p-4 border rounded-lg font-mono text-sm resize-none focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="File content..."
                  />
                )}
              </CardContent>
            </>
          ) : (
            <CardContent className="py-24 text-center">
              <BookOpen className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="font-medium text-lg">Select a file to view</p>
              <p className="text-sm text-gray-500 mt-1">
                Browse documentation, NDA templates, and configuration files
              </p>
            </CardContent>
          )}
        </Card>
      </div>
    </div>
  );
}
